import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { UserInfos } from '../types';

import { RootState } from 'src/app/store';
import { fetchUserInfo } from '../services/user.service';

interface UsersState {
  error: string | undefined;
  isUsersLoading: boolean;
  users: UserInfos[];
  activeRequestId: string | null;
}

const initialState: UsersState = {
  isUsersLoading: true,
  users: [],
  error: undefined,
  activeRequestId: null
};

export const addUsers = createAsyncThunk<
  UserInfos[],
  string[],
  { rejectValue: Error; state: RootState; requestId: string }
>('usersRole/fetch', async (listUsersIds, { rejectWithValue, getState, requestId }) => {
  try {
    let users: UserInfos[] = [];
    const setCreatorNotInUsers = new Set<string>();
    listUsersIds.forEach((userId) => {
      if (!getState().user.users?.find((user) => user.id === userId)) {
        setCreatorNotInUsers.add(userId);
      }
    });
    if (setCreatorNotInUsers.size > 0) {
      const usersTemp = await fetchUserInfo(Array.from(setCreatorNotInUsers.values())).then(
        (response) => response.data
      );
      const { activeRequestId } = (getState() as RootState).user;
      if (requestId !== activeRequestId) {
        return rejectWithValue(new Error('Request ignored'));
      }
      users = [...usersTemp];
    }
    return users;
  } catch (err) {
    const error = err as Error;
    if (!error.message) {
      throw error;
    }
    return rejectWithValue(error);
  }
});

export const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(addUsers.pending, (state, action) => {
        state.isUsersLoading = true;
        state.users = [];
        state.error = undefined;
        state.activeRequestId = action.meta.requestId;
      })
      .addCase(addUsers.fulfilled, (state, { payload, meta }) => {
        if (meta.requestId === state.activeRequestId) {
          state.isUsersLoading = false;
          state.users = state.users.concat(payload);
          state.activeRequestId = null;
          state.error = undefined;
        }
      })
      .addCase(addUsers.rejected, (state, { error, meta }) => {
        if (meta.requestId === state.activeRequestId) {
          state.isUsersLoading = false;
          state.error = error.message;
          state.users = [];
          state.activeRequestId = null;
        }
      });
  }
});

export default usersSlice.reducer;
